import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { connectApi } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Landmark, Loader2, CheckCircle2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

// Stripe Connect onboarding return/refresh landing.
// mode="return": Stripe sent the Commander back after onboarding — show live status.
// mode="refresh": the Account Link expired — mint a fresh one and re-route.
export default function ConnectReturn({ mode = "return" }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    connectApi.status().then(setStatus).catch(() => setStatus({ status: "error" }));
  }, []);

  const resume = async () => {
    setBusy(true);
    try {
      const r = await connectApi.onboardingLink();
      window.location.href = r.onboarding_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create onboarding link");
      setBusy(false);
    }
  };

  const done = status?.status === "active" || status?.details_submitted;

  return (
    <div className="min-h-screen w-full flex items-center justify-center uc-grain" style={{ background: "#0A0A0A" }} data-testid="connect-return-page">
      <div className="uc-card p-8 max-w-md w-full text-white">
        <div className="uc-chip mb-4"><Landmark size={11} className="text-amber-400"/> STRIPE CONNECT</div>
        {!status ? (
          <div className="flex items-center gap-2 text-white/60"><Loader2 size={16} className="animate-spin"/> Checking onboarding status…</div>
        ) : mode === "refresh" || (!done && status.status !== "error") ? (
          <>
            <h1 className="font-serif-display text-3xl mb-2">Onboarding paused</h1>
            <p className="text-white/60 text-sm mb-6">Your Stripe onboarding link expired or was interrupted. Resume where you left off.</p>
            <Button data-testid="connect-resume-btn" onClick={resume} disabled={busy} className="uc-btn-amber rounded-none h-11 w-full">
              {busy ? <Loader2 size={14} className="mr-2 animate-spin"/> : <RefreshCw size={14} className="mr-2"/>}
              Resume Stripe Onboarding
            </Button>
          </>
        ) : done ? (
          <>
            <div className="flex items-center gap-2 text-emerald-400 mb-2"><CheckCircle2 size={20}/><h1 className="font-serif-display text-3xl">Details submitted</h1></div>
            <p className="text-white/60 text-sm mb-6">
              {status.charges_enabled
                ? "Your connected account is ACTIVE — payments are enabled."
                : "Stripe is verifying your details. Your account will activate shortly."}
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif-display text-3xl mb-2">Status unavailable</h1>
            <p className="text-white/60 text-sm mb-6">Could not verify your Connect account right now. Head to your dashboard and retry later.</p>
          </>
        )}
        <Link to="/" className="block mt-4">
          <Button data-testid="connect-to-dashboard-btn" variant="ghost" className="rounded-none h-11 w-full border border-white/10 text-white/70 hover:bg-white/5">
            Enter Command Center
          </Button>
        </Link>
      </div>
    </div>
  );
}
